import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { QrCode, ScanLine, Upload, Download, Printer, Trash2, Link2 } from 'lucide-react';
import { parseApiError } from '../lib/parseApiError';
import BarcodeScannerModal from './BarcodeScannerModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog QR Code Agenzia delle Entrate per un impianto (Registratore Telematico).
 * Inserimento: scansione fotocamera / URL manuale / upload file.
 * Uso: anteprima, download targhetta PDF 7x3,5cm (logo AdE + QR), stampa.
 */
export default function QrAdeDialog({ open, onOpenChange, impianto, onSaved }) {
  const [qrPreview, setQrPreview] = useState(null); // objectURL PNG
  const [hasQr, setHasQr] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [savedUrl, setSavedUrl] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const impiantoId = impianto?.id;
  const impiantoQrUrl = impianto?.qr_ade_url || '';

  const loadPreview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/impianti/${impiantoId}/qr-ade/png`, { responseType: 'blob' });
      setQrPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
      setHasQr(true);
    } catch {
      setQrPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setHasQr(false);
    }
  }, [impiantoId]);

  useEffect(() => {
    if (open && impiantoId) {
      setUrlInput('');
      setSavedUrl(impiantoQrUrl);
      loadPreview();
    }
  }, [open, impiantoId, impiantoQrUrl, loadPreview]);

  const isValidUrl = (v) => /^https?:\/\//i.test((v || '').trim());

  const salva = async (payload) => {
    setSaving(true);
    try {
      await axios.put(`${API}/impianti/${impianto.id}/qr-ade`, payload);
      toast.success(payload.qr_url || payload.qr_image_b64 ? '✅ QR AdE salvato' : 'QR AdE rimosso');
      setSavedUrl(payload.qr_url ? payload.qr_url.trim() : '');
      await loadPreview();
      onSaved?.();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore salvataggio QR'));
    } finally {
      setSaving(false);
    }
  };

  const handleScan = (code) => {
    if (!code) return;
    const clean = String(code).trim();
    if (!isValidUrl(clean)) {
      toast.error(`Il QR letto non è un link valido: "${clean.slice(0, 60)}". Riprova la scansione con la fotocamera.`, { duration: 8000 });
      return;
    }
    salva({ qr_url: clean });
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_800_000) {
      toast.error('File troppo grande (max 1,8 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => salva({ qr_image_b64: reader.result });
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleDownloadTarghetta = async (stampa = false) => {
    try {
      const res = await axios.get(`${API}/impianti/${impianto.id}/qr-ade/targhetta`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (stampa) {
        const w = window.open(url);
        if (w) w.onload = () => w.print();
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `qr_ade_${impianto.matricola || impianto.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      toast.success(stampa ? '🖨️ Targhetta pronta per la stampa' : '✅ Targhetta scaricata (7 × 3,5 cm)');
    } catch (error) {
      toast.error(parseApiError(error, 'Errore generazione targhetta'));
    }
  };

  if (!impianto) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md rounded-none max-h-[90vh] overflow-y-auto" data-testid="qr-ade-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono uppercase tracking-wider">
              <QrCode className="h-5 w-5 text-cyan-500" />
              QR Agenzia delle Entrate
            </DialogTitle>
            <DialogDescription>
              {impianto.marca} {impianto.modello} — Matr. <span className="font-mono font-semibold">{impianto.matricola || 'n/d'}</span>
            </DialogDescription>
          </DialogHeader>

          {hasQr ? (
            <div className="space-y-3">
              <div className="flex justify-center p-4 bg-white border border-border">
                {qrPreview && <img src={qrPreview} alt="QR AdE" className="max-h-48" data-testid="qr-ade-preview" />}
              </div>
              {savedUrl ? (
                <div className="space-y-1.5">
                  <div className="bg-muted/60 border border-border px-2 py-1.5">
                    <p className="text-[10px] font-mono uppercase text-muted-foreground mb-0.5">Link salvato nel QR:</p>
                    <p className="text-[11px] font-mono break-all leading-tight" data-testid="qr-ade-saved-url">{savedUrl}</p>
                  </div>
                  {!/agenziaentrate/i.test(savedUrl) && (
                    <p className="text-[11px] text-amber-600 bg-amber-50 dark:bg-amber-950/30 border border-amber-500/40 px-2 py-1" data-testid="qr-ade-url-warning">
                      ⚠️ Il link non sembra del portale Agenzia delle Entrate: potrebbe essere stato letto male. Verificalo con il pulsante qui sotto, oppure rimuovilo e riscansiona con la fotocamera.
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-none w-full text-xs"
                    onClick={() => window.open(savedUrl, '_blank', 'noopener,noreferrer')}
                    data-testid="qr-ade-test-link-btn"
                  >
                    <Link2 className="h-3.5 w-3.5 mr-2" /> Verifica: apri il link nel browser
                  </Button>
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground text-center">QR da immagine caricata dal portale AdE (contenuto originale)</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Button className="rounded-none" onClick={() => handleDownloadTarghetta(false)} data-testid="qr-ade-download-btn">
                  <Download className="h-4 w-4 mr-2" /> Scarica Targhetta
                </Button>
                <Button variant="outline" className="rounded-none" onClick={() => handleDownloadTarghetta(true)} data-testid="qr-ade-print-btn">
                  <Printer className="h-4 w-4 mr-2" /> Stampa
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground text-center">Targhetta in formato ufficiale 7 × 3,5 cm con logo AdE — da applicare sul registratore</p>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-none text-destructive w-full"
                onClick={() => salva({})}
                disabled={saving}
                data-testid="qr-ade-remove-btn"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Rimuovi QR (per sostituirlo)
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Nessun QR salvato. Aggiungilo in uno di questi 3 modi:</p>
              <Button className="rounded-none w-full justify-start" variant="outline" onClick={() => setScannerOpen(true)} data-testid="qr-ade-scan-btn">
                <ScanLine className="h-4 w-4 mr-2 text-cyan-600" />
                1. Scansiona il QR sulla cassa (fotocamera)
              </Button>
              <div className="space-y-1">
                <Label className="text-xs font-mono uppercase flex items-center gap-1"><Link2 className="h-3 w-3" /> 2. Incolla il link del QR</Label>
                <div className="flex gap-2">
                  <Input
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://ivaservizi.agenziaentrate.gov.it/..."
                    className="rounded-none text-xs font-mono"
                    data-testid="qr-ade-url-input"
                  />
                  <Button className="rounded-none shrink-0" disabled={!urlInput.trim() || saving} onClick={() => {
                    const clean = urlInput.trim();
                    if (!isValidUrl(clean)) {
                      toast.error('Il link deve iniziare con https:// — copialo dal portale AdE');
                      return;
                    }
                    salva({ qr_url: clean });
                  }} data-testid="qr-ade-url-save-btn">
                    Salva
                  </Button>
                </div>
              </div>
              <Button className="rounded-none w-full justify-start" variant="outline" onClick={() => fileRef.current?.click()} data-testid="qr-ade-upload-btn">
                <Upload className="h-4 w-4 mr-2 text-cyan-600" />
                3. Carica il file dal portale AdE (JPG/PNG)
              </Button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleFile} />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BarcodeScannerModal
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onScan={handleScan}
        title="Inquadra il QR code sulla cassa"
      />
    </>
  );
}
